import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ClientsIcon, SettingsIcon, UserIcon, ChevronDownIcon } from '../../assets/icons';
import { useTranslation } from 'react-i18next';
import { useGetUserSessionInfo } from '../../hooks/user/useGetUserSessionInfo';
import type { GetUserResponse, GetUser, User } from '../../generated/graphql';
import { useContextStore } from '../../hooks/context/useContextStore';
import ConfirmationModal from '../Modals/ConfirmationModal';

interface SettingsMenuItem {
  id: string;
  name: string;
  icon: string;
  link: string;
}

const isGetUser = (response: GetUserResponse): response is GetUser => {
  return 'user' in response;
};

const UserSettingsPopover = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const { clientUser, signOut, signOutLoading } = useContextStore();
  const { userInfo } = useGetUserSessionInfo();
  const sessionUser = userInfo && isGetUser(userInfo) ? userInfo.user : undefined;
  const user = sessionUser || clientUser;
  const name = user ? `${user.firstName} ${user.lastName?.charAt(0)}` : '';
  /**
   * The unverified mark the SP app carries on the profile: a small "!" on the
   * avatar, so the state is visible from every screen without a banner sitting
   * on top of the dashboard. The banner itself lives on Profile, which is one
   * click away in this very menu.
   */
  const isEmailUnconfirmed = (user as User | undefined)?.isEmailConfirmed === false;
  const popoverRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const menuItems: SettingsMenuItem[] = [
    {
      id: 'profile',
      name: t('profile'),
      icon: UserIcon,
      link: '/settings?tab=profile',
    },
    {
      id: 'settings',
      // No tab param: lands on the section list, which on phones is the first level.
      name: t('settings'),
      icon: SettingsIcon,
      link: '/settings',
    },
  ];

  const toggleMenu = () => setIsOpen(prev => !prev);

  return (
    <div className="relative text-sm" ref={popoverRef}>
      <button
        onClick={toggleMenu}
        className="flex items-center gap-2 px-1 py-1 rounded-ds-default hover:bg-ds-bg/50 transition-colors focus:outline-none"
      >
        {/* The badge is a sibling of the clipped circle, not a child: the
            avatar's `overflow-hidden` would cut it in half. */}
        <div className="relative w-8 h-8 flex-shrink-0">
          <div className="w-8 h-8 rounded-full overflow-hidden">
            <img
              src={(user as User)?.avatarUrl || ClientsIcon}
              alt={t('user')}
              className="w-full h-full object-cover rounded-full"
            />
          </div>
          {isEmailUnconfirmed && (
            <>
              <span
                aria-hidden="true"
                title={t('verifyYourEmailAddress')}
                className="absolute -bottom-0.5 -right-0.5 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-[#E16934] text-[9px] font-bold leading-none text-white ring-2 ring-white"
              >
                !
              </span>
              <span className="sr-only">{t('verifyYourEmailAddress')}</span>
            </>
          )}
        </div>
        <span className="hidden sm:block text-sm font-medium tracking-[0.02em] leading-[150%] text-ds-text-primary truncate max-w-[120px]">
          {name}
        </span>
        <img
          src={ChevronDownIcon}
          alt=""
          className="w-4 h-4 hidden sm:block"
          style={{
            filter:
              'brightness(0) saturate(100%) invert(56%) sepia(6%) saturate(927%) hue-rotate(191deg) brightness(92%) contrast(89%)',
          }}
        />
      </button>

      <div
        className={`absolute right-0 top-[44px] z-10 w-[220px] rounded-[10px] bg-white border border-ds-border shadow-ds-card pt-4 transition-all duration-300 text-ds-text-secondary ${
          isOpen ? 'opacity-100 scale-100' : 'opacity-0 scale-95 pointer-events-none'
        }`}
      >
        <ul className="space-y-1 px-1">
          {menuItems.map(item => (
            <li key={item.id}>
              <Link
                to={item.link}
                onClick={() => setIsOpen(false)}
                className="flex items-center gap-2 py-2 px-3 cursor-pointer hover:bg-ds-bg rounded-ds-default text-sm tracking-[0.02em]"
              >
                <img
                  src={item.icon}
                  alt={item.name}
                  className="w-5 h-5"
                  style={{
                    filter:
                      'brightness(0) saturate(100%) invert(45%) sepia(8%) saturate(1051%) hue-rotate(191deg) brightness(92%) contrast(86%)',
                  }}
                />
                <span>{item.name}</span>
              </Link>
            </li>
          ))}
        </ul>
        <hr className="border-t border-ds-border-light my-3" />
        <button
          onClick={() => setShowLogoutModal(true)}
          disabled={signOutLoading}
          className="h-8 border border-ds-error rounded-ds-default text-ds-error px-3 mx-3 mb-3 text-sm font-medium disabled:opacity-50 hover:bg-ds-error-bg transition-colors"
        >
          <span>{t('logOut')}</span>
        </button>
      </div>

      <ConfirmationModal
        isOpen={showLogoutModal}
        title={t('logOut')}
        message={t('areYouSureToUseSecretLogout')}
        confirmText={t('logOut')}
        cancelText={t('cancel')}
        onConfirm={async () => {
          setShowLogoutModal(false);
          await signOut();
        }}
        onClose={() => setShowLogoutModal(false)}
        loading={signOutLoading}
        variant="danger"
      />
    </div>
  );
};

export default UserSettingsPopover;
